/**
 * useMe — current authenticated user. Backed by GET /auth/me.
 *
 * The query is gated on `getAccess()` so it doesn't fire pre-login (or
 * before silent-refresh has populated the access token on app boot).
 * staleTime 30s matches the server-side access-token half-life pattern —
 * sub-second refetches would only replay the same payload.
 *
 * Блок 2 · Ф8 · В-О2: профиль несёт права человека (`rights`). Читает их
 * только `useRights()`; при переходе между страницами профиль перезапрашивает
 * `useSessionFreshness` — мимо порога свежести.
 *
 * Whatever shape /auth/me returns is forwarded as-is so call-sites stay
 * compatible with future BE additions (e.g. mustChangePassword bit).
 */
import { useQuery } from '@tanstack/react-query';

import { RoleSlug } from '@aris/shared';
import type { AccessLevel, ActorRights, NotificationPrefs, VkBindingFragment } from '@aris/shared';

import { apiFetch, getAccess } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

/**
 * Mirror of the API's /auth/me response (apps/api/src/auth/auth.controller.ts
 * — `AuthUserExtended`). We don't import the BE type to keep packages
 * decoupled; the BE owns the source of truth.
 *
 * Wave-5 fix 5.x.10: `accessLevel` is now the canonical string union
 * imported from `@aris/shared` (was `number` in Batch 1 — a stale type
 * that drifted from the actual JSON the BE produced).
 */
/**
 * Значение настройки сервера `WORKLOAD_VIEW_LEVEL`, как его отдаёт `/auth/me`.
 *
 * ⚠️ Ф8 · В-С (решение владельца 11.10.2026): ЗНАЧЕНИЙ ДВА. Третье,
 * `'employee'`, упразднено — оно отнимало у руководителей отделов и
 * администраторов положенное по должности (Р-229); сервер с ним не
 * поднимается, и в ответе его быть не может.
 *
 * ⚠️ Оставшиеся значения на экране НЕ РАЗЛИЧАЮТСЯ: область человека на
 * «Загруженности» (`lib/workload-scope.ts`) от значения не зависит. Поле
 * приходит и проносится до функции области, но ни одно решение на нём не
 * стоит; судьба самой настройки — решение владельца.
 */
export type WorkloadViewLevel = 'manager' | 'admin';

export interface MeUser {
  id: string;
  email: string;
  phone: string;
  status: string;
  departmentId: number | null;
  roleId: number;
  roleSlug: string;
  accessLevel: AccessLevel;
  /**
   * `Role.isAssignable` должности (Ф8 · В-Э, фикс-пак 1). Вместе с уровнем
   * отвечает на «открыт ли экран прав» — тем же предикатом, что страж маршрутов
   * на сервере; читает его `canManageRights`. Необязательное: образ API до
   * волны в ответе входа поля не несёт, и отсутствие читается как «закрыто».
   */
  isAssignable?: boolean;
  mustChangePassword: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  /**
   * Optional on the wire — BE adds this in Phase 3 W2.T3 but older
   * snapshot images may still serve the Phase 2 envelope. Treat
   * `undefined` as `'admin'` (full UI) at the call site so the FE
   * does not silently downgrade after a partial deploy.
   */
  workloadViewLevel?: WorkloadViewLevel;
  /**
   * Optional on the wire — BE adds `department.name` to the `/auth/me`
   * envelope as part of Phase 5 FIX-PACK FA-1 (closes F-01: dashboard
   * subtitle PII leak). Older API images may not yet ship the field, so
   * we treat `undefined` as `null` at the call site and the dashboard
   * header renders «—» as the dept-name fallback. Never derived from
   * `email` — using PII as a label was the original BLOCK.
   */
  departmentName?: string | null;
  /**
   * M2 P7 W5: per-user notification preferences for VK Teams reminders.
   * Optional on the wire so a pre-W5 API bundle (during rolling deploy)
   * does not crash the FE — the consumer in `<ProfilePage>` falls back
   * to the schema defaults (`{ remindersEnabled: true, reminderTime:
   * '18:00', reminderDaysOfWeek: [1..5] }`) when undefined and shows
   * an inline notice asking the user to refresh.
   */
  notificationPrefs?: NotificationPrefs;
  /**
   * Feature 1 «Помощник ГИПа» (Wave 5). For an ASSISTANT_GIP user, the id of
   * the GIP they assist; `null` for everyone else. The BE `/auth/me` (Wave 2)
   * always emits this now, but we keep it `| null` (not optional) at the type
   * level because the shared `MeResponseSchema` types it `.nullable().optional()`
   * — a pre-Wave-2 API image during a rolling deploy would omit it. Consumers
   * MUST handle `undefined` as «not an assistant» (same surface as `null`):
   * the `isGip` derivation in <ProjectsDetailPage> reads it via
   * `me.assistantOfUserId === project.gip.id`, which is safely `false` for
   * both `null` and `undefined`.
   */
  assistantOfUserId?: string | null;
  /**
   * M2 P7 W5: the user's current bot binding fragment, or `null`
   * when no binding exists. Optional on the wire for the same rolling-
   * deploy reason — pre-W5 bundles omit the key entirely. `null` and
   * `undefined` are NOT equivalent: `null` is "BE confirmed unbound",
   * `undefined` is "BE is too old to tell us"; the latter case keeps
   * the «Привязать» button visible but the consumer renders an inline
   * notice that the API may not be fully deployed yet.
   *
   * 🚫 ⚠️ Ф5 · В8-b.1, ФИКС-ПАК КРУГА (25.08.2026) — СНЯТО СЛОВО «VK Teams»
   * из первой строки: ~~«the user's current VK Teams binding fragment»~~.
   * Фрагмент несёт привязку ЛЮБОГО канала и заведомо бывает телеграмным
   * (**Р-87**), а с той же волны — ещё и поле `channel`. Имя `vkBinding`
   * при этом ОСТАВЛЕНО намеренно: переименование утащило бы за собой
   * контракт `@aris/shared`, типы бота и форму аудит-снимка, и Р-87 его
   * прямо запрещает. То есть имя устарело сознательно, а докстринг —
   * по недосмотру; исправлено второе.
   */
  vkBinding?: VkBindingFragment;
  /**
   * Блок 2 · Ф8 · В-О2 — поправки к должности: собственные строки человека и
   * запреты ГИПа, чьим помощником он служит. Формат — `ActorRightsSchema`
   * (`@aris/shared`); то же поле несёт `user` в ответе логина.
   *
   * ⚠️ НЕ ЧИТАТЬ НАПРЯМУЮ. Это вход ступени 1, а не ответы «можно / нельзя»;
   * единственный читатель — `useRights()` (`hooks/use-rights.ts`), и он же
   * держит правило «не загружено — закрыто»: `undefined` здесь (образ API до
   * волны, неполный засев) означает «закрыто всё», а не «поправок нет».
   */
  rights?: ActorRights;
}

export function useMe() {
  return useQuery<MeUser>({
    queryKey: qk.auth.session(),
    queryFn: () => apiFetch<MeUser>('/auth/me'),
    staleTime: 30_000,
    // Блок 2 · Ф8 · В-О2 — ЯВНО, а не по умолчанию клиента: профиль несёт
    // права, и по возврату фокуса в окно он НЕ перезапрашивается. Возврат к
    // вкладке — не переход (Р-198), а открытая вкладка принудительно не
    // перезагружается (Р-217 г). Обновляет права переход между страницами —
    // `useSessionFreshness`; `staleTime` выше — порог свежести, не таймер.
    refetchOnWindowFocus: false,
    enabled: getAccess() !== null,
  });
}

/**
 * ПРАВИЛО «КОГДА УВОДИТЬ НА ВХОД» — одно на всех стражей (Блок 2 · Ф8 ·
 * В-О2, фикс-пак 2).
 *
 * Сессия потеряна в двух случаях, и только в них:
 *   · профиля нет вовсе — первая загрузка завершилась отказом;
 *   · запрос профиля в ошибке И токена нет — настоящий 401: `lib/fetcher.ts`
 *     сбросил токен после неудачного обновления сессии.
 *
 * ⚠️ ОШИБКА ЗАПРОСА ПРОФИЛЯ САМА ПО СЕБЕ — НЕ ПОТЕРЯ СЕССИИ. С волны В-О2
 * профиль перезапрашивается на каждом переходе, и отказ такого перезапроса —
 * обрыв сети, 502 от прокси, 429 от ограничителя — при живой сессии не должен
 * менять экран: клиент запросов данные при фоновой ошибке не стирает, человек
 * работает с прежним профилем, следующий переход пробует снова. Сервер читает
 * права из базы на каждом запросе — прежний профиль в вебе ничего не открывает.
 *
 * ⚠️ ПОЧЕМУ ФУНКЦИЯ, А НЕ УСЛОВИЕ НА МЕСТЕ. Условие `isError || !me` стояло в
 * ДВУХ стражах (`<RequireAuth>` и `<RequireAccessLevel>`). Фикс-пак 1 исправил
 * первый и проверил его на маршруте без второго; на стенде человека выбрасывал
 * второй. Новый страж обязан звать эту функцию, а не писать условие заново —
 * это держит тест по настоящей таблице маршрутов
 * (`routes-session-failure.test.tsx`).
 *
 * ⚠️ `isError` ПЕРЕДАЁТСЯ ИЗ РЕЗУЛЬТАТА `useMe()` — и вызывающий тем самым его
 * ЧИТАЕТ. Это несущее: клиент запросов перерисовывает компонент только по тем
 * полям результата, которые тот читал; страж, не читавший `isError`, не
 * перерисуется при отказе с неизменными данными и сброшенный токен не заметит.
 */
export function isSessionLost(session: {
  me: MeUser | null | undefined;
  isError: boolean;
}): boolean {
  return !session.me || (session.isError && getAccess() === null);
}

/**
 * True when a `me` object is present but has NOT yet hydrated its
 * `accessLevel`. This window exists because a partial user shape (no
 * `accessLevel`) can briefly sit in the session cache; with the BE
 * fix-pack-2 (login/refresh now return `accessLevel`) the only remaining
 * source is a future payload drift, but every consumer that branches on
 * `me.accessLevel` MUST treat this state as "still loading" rather than
 * acting on the missing field — otherwise RBAC route guards deny, the
 * home redirect strands the user on the wrong page, and the sidebar
 * hides all gated nav. Single source of truth so the three consumers
 * (RequireAccessLevel, RoleHomeRedirect, RootLayout) stay consistent.
 *
 * `me == null` (undefined while loading, or null) is NOT "hydrating" by
 * this predicate — that state is already handled by each consumer's own
 * loading / unauthenticated branch; this helper isolates the narrower
 * "present-but-incomplete" case.
 */
export function isMeHydrating(me: MeUser | null | undefined): boolean {
  return me != null && me.accessLevel == null;
}

/**
 * True when the current user is an ASSISTANT_GIP.
 *
 * Under the GLOBAL admin-appointment rule (assistant-parity) the assistant
 * is a full generic-admin for NON-admin user management + projects, so this
 * predicate NO LONGER gates any action affordance (project-create,
 * user-invite, and user-management all flow through the ordinary ADMIN
 * checks; the ADMIN-tier appointment clamp lives on `accessLevel` — only a
 * SUPERVISOR sees admin-tier options). It is retained purely as a binding-UX
 * signal for consumers that need to distinguish an assistant from a generic
 * admin (e.g. bound-ГИП display), and treats a missing/partial `roleSlug` as
 * «not an assistant». The BE remains the security boundary and 403/422s
 * regardless — this is purely UX.
 */
export function isAssistantGip(me: MeUser | null | undefined): boolean {
  return me?.roleSlug === RoleSlug.ASSISTANT_GIP;
}
